import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

import { CreditPackCard } from '@/components/ai-tools/credit-pack-card';
import { listCreditPacks } from '@/config/pricing';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { formatUsd } from '@/lib/ai-tools/format';
import { apiPost } from '@/lib/api-client';
import { track } from '@/lib/track';
import { m } from '@/paraglide/messages.js';

const POPULAR_PACK = 'credits_popular';

/** One-time credit packs; checkout returns to `returnPath`. */
export function CreditPacks({
  slug,
  returnPath,
  standardCredits,
  unit = 'video',
}: {
  /** The tool page this section sits on, for the funnel events; absent on the settings page. */
  slug?: string;
  returnPath: string;
  standardCredits: number;
  /** What one standard render makes; the pack lines count these. */
  unit?: 'video' | 'image';
}) {
  const { requireAuth, gate } = useAuthGate('purchase');
  const checkout = useMutation({
    mutationFn: (productId: string) => {
      track('tool_pack_click', { tool: slug ?? 'settings', pack: productId });
      return apiPost<{ checkout_url: string }>('/api/payment/checkout', { product_id: productId, redirect: returnPath });
    },
    onSuccess: (data, productId) => {
      if (data?.checkout_url) {
        track('tool_checkout_started', { tool: slug ?? 'settings', pack: productId, source: 'section' });
        window.location.assign(data.checkout_url);
      } else {
        toast.error(m['directory.ai_tools.error.generic']());
      }
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="space-y-4">
      {gate}
      <div className="grid gap-6 sm:grid-cols-3 lg:gap-8">
        {listCreditPacks().map((pack) => {
          const count = Math.floor(pack.credits / standardCredits);
          const lines = [
            m['directory.ai_tools.packs.credits']({ credits: pack.credits.toLocaleString() }),
            unit === 'image' ? m['directory.ai_tools.packs.images']({ count }) : m['directory.ai_tools.packs.videos']({ count }),
          ];
          if (count > 0) {
            const price = formatUsd(Math.round(pack.priceInCents / count));
            lines.push(
              unit === 'image' ? m['directory.ai_tools.packs.per_image']({ price }) : m['directory.ai_tools.packs.per_video']({ price }),
            );
          }
          return (
            <CreditPackCard
              key={pack.productId}
              name={pack.productName}
              price={formatUsd(pack.priceInCents)}
              lines={lines}
              badge={pack.productId === POPULAR_PACK ? m['directory.ai_tools.packs.popular']() : undefined}
              buyLabel={m['directory.ai_tools.packs.buy']({ price: formatUsd(pack.priceInCents) })}
              busyLabel={m['directory.ai_tools.packs.redirecting']()}
              busy={checkout.isPending && checkout.variables === pack.productId}
              disabled={checkout.isPending}
              onBuy={() => requireAuth(() => checkout.mutate(pack.productId))}
            />
          );
        })}
      </div>
      <p className="text-center text-xs text-muted-foreground">{m['directory.ai_tools.packs.secure']()}</p>
    </div>
  );
}
